import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type MutableRefObject, type ReactNode } from 'react'
import { useLocation } from 'react-router'
import { toast } from 'sonner'
import { getViewAs } from '@/features/view-as'
import { activityBetween, recordActivity } from './activity'
import { reportSpot, useOfficeRoster, useTeamDay, type TeamDay } from './api'
import { buildNav, layoutOffice, type Nav, type OfficePerson, type Pos, type World } from './arena'
import { ingestSpeech } from './chatLog'
import { useProximityCalls, type RemoteMedia } from './useProximityCalls'

export interface OfficeLive {
  data: (TeamDay & { fetchedAt: number }) | undefined
  isLoading: boolean
  isError: boolean
  roster: OfficePerson[] | undefined
  world: World | null
  nav: Nav | null
  /** Tell everyone right now, rather than at the next regular report: a bubble or an emote. */
  announce: (extra?: { say?: string; emote?: string }) => void
  mic: boolean
  cam: boolean
  /** Turn the microphone and camera on or off. Asks the browser for permission the first time. */
  setMedia: (mic: boolean, cam: boolean) => Promise<void>
  localStream: MediaStream | null
  calls: RemoteMedia[]
  /** False while viewing as someone else: you may look, but nothing is reported or connected. */
  writes: boolean
}

const Ctx = createContext<OfficeLive | null>(null)
/** Where you stand, in a context of its own: a ref is not something to pass around inside a value that is rendered from. */
const PosCtx = createContext<MutableRefObject<Pos | null> | null>(null)

export function usePosRef(): MutableRefObject<Pos | null> {
  const v = useContext(PosCtx)
  if (!v) throw new Error('usePosRef needs an OfficeLiveProvider')
  return v
}

export function useOfficeLive(): OfficeLive {
  const v = useContext(Ctx)
  if (!v) throw new Error('useOfficeLive needs an OfficeLiveProvider')
  return v
}

function RemoteAudio({ stream }: { stream: MediaStream }) {
  return (
    <audio
      autoPlay
      playsInline
      className="hidden"
      ref={(el) => {
        if (el && el.srcObject !== stream) {
          el.srcObject = stream
          void el.play().catch(() => undefined)
        }
      }}
    />
  )
}

/**
 * Presence for the whole app, not one page: for as long as Tapwork is open you are in the office,
 * standing where you last stood (your own desk until you walk somewhere), so the widget on every
 * page and the people near you see the same floor. The office page is only where you move.
 */
export function OfficeLiveProvider({ children }: { children: ReactNode }) {
  // Looking as someone else is read-only: nothing is reported, and no microphone or camera is touched.
  const writes = getViewAs() === null
  const { pathname } = useLocation()
  const onOffice = pathname.endsWith('/office')
  const team = useTeamDay(true, onOffice ? true : 'ambient')
  const roster = useOfficeRoster().data
  const world = useMemo(() => (roster ? layoutOffice(roster) : null), [roster])
  const nav = useMemo(() => (world ? buildNav(world) : null), [world])
  const data = team.data
  const meId = data?.me ?? ''
  const posRef = useRef<Pos | null>(null)

  const [mic, setMic] = useState(false)
  const [cam, setCam] = useState(false)
  const [localStream, setLocalStream] = useState<MediaStream | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const flags = useRef({ mic: false, cam: false })
  useEffect(() => {
    flags.current = { mic, cam }
  }, [mic, cam])

  const setMedia = useCallback(async (nextMic: boolean, nextCam: boolean) => {
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
    if (!nextMic && !nextCam) {
      setMic(false)
      setCam(false)
      setLocalStream(null)
      return
    }
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        audio: nextMic,
        video: nextCam ? { width: { ideal: 320 }, height: { ideal: 240 }, facingMode: 'user' } : false,
      })
      streamRef.current = s
      setMic(nextMic)
      setCam(nextCam)
      setLocalStream(s)
    } catch {
      toast.error('Could not turn that on. Check that your browser is allowed to use the microphone and camera for Tapwork.')
      setMic(false)
      setCam(false)
      setLocalStream(null)
    }
  }, [])
  useEffect(
    () => () => {
      streamRef.current?.getTracks().forEach((t) => t.stop())
    },
    [],
  )

  const home = world?.desks.find((d) => d.userId === meId)?.seat ?? world?.spawn ?? null
  const homeRef = useRef(home)
  useEffect(() => {
    homeRef.current = home
    // Until you walk somewhere you are at your own desk, so everything that reads your spot has one.
    if (home && !posRef.current) posRef.current = home
  }, [home])

  const send = useCallback(
    (extra?: { say?: string; emote?: string }) => {
      const p = posRef.current ?? homeRef.current
      if (!p) return
      reportSpot({ ...p, ...extra, mic: flags.current.mic, cam: flags.current.cam }).catch(() => undefined)
    },
    [],
  )

  // Report where you stand every couple of seconds for as long as the app is open, on any page and
  // in a background tab (the browser slows that tab's timer, which the server's window allows for).
  useEffect(() => {
    if (!writes || !meId || !world) return
    let sent = { x: -1, y: -1, at: 0, mic: false, cam: false }
    const tick = () => {
      const p = posRef.current ?? homeRef.current
      if (!p) return
      const at = Date.now()
      const { mic: m, cam: c } = flags.current
      if (Math.hypot(p.x - sent.x, p.y - sent.y) <= 3 && at - sent.at < 8_000 && m === sent.mic && c === sent.cam) return
      sent = { x: p.x, y: p.y, at, mic: m, cam: c }
      reportSpot({ ...p, mic: m, cam: c }).catch(() => undefined)
    }
    tick()
    const id = window.setInterval(tick, 2_000)
    return () => window.clearInterval(id)
  }, [writes, meId, world])

  const people = data?.people
  const calls = useProximityCalls({ enabled: writes && Boolean(localStream), meId, people: people ?? [], posRef: posRef, localStream })

  // What others did since the last refresh, and what they said, kept for the widget and the page.
  const before = useRef<TeamDay['people'] | null>(null)
  useEffect(() => {
    if (!people || !world) return
    recordActivity(activityBetween(before.current, people, world, meId, Date.now()))
    before.current = people
    ingestSpeech(people, meId)
  }, [people, world, meId])

  const value = useMemo<OfficeLive>(
    () => ({
      data,
      isLoading: team.isLoading || !roster,
      isError: team.isError,
      roster,
      world,
      nav,
      announce: send,
      mic,
      cam,
      setMedia,
      localStream,
      calls,
      writes,
    }),
    [data, team.isLoading, team.isError, roster, world, nav, send, mic, cam, setMedia, localStream, calls, writes],
  )

  return (
    <PosCtx.Provider value={posRef}>
      <Ctx.Provider value={value}>
        {children}
        {calls.map((r) => (
          <RemoteAudio key={r.userId} stream={r.stream} />
        ))}
      </Ctx.Provider>
    </PosCtx.Provider>
  )
}
